import React, { useState, useEffect } from 'react';
import { ArrowLeft, Users, User, Mail, Phone, MapPin, Award, Calendar, Building, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getText, getCurrentLanguage } from '../../services/dataSync';

const CommitteePage = () => {
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  // Committee members with photo, name, and role (stable order)
  const committeeMembersWithRoles = [
    { name: getText('about.structure.leadership.president.name') || 'Mr Kailas Narke', roleKey: 'about.structure.leadership.president.position', photo: '/images/4.jpg' },
    { name: getText('about.structure.leadership.vicePresident.name') || 'Mr Javed Inamdar', roleKey: 'about.structure.leadership.vicePresident.position', photo: '/images/2.jpg' },
    { name: getText('about.structure.leadership.secretary.name') || 'Ms Deepa Mahajan', roleKey: 'about.structure.leadership.secretary.position', photo: '/images/deepa.jpeg' },
    { name: getText('about.structure.leadership.treasurer.name') || 'Mr Ramdas Napte', roleKey: 'about.structure.leadership.treasurer.position', photo: '/images/napte.jpeg' },
    { name: getText('about.committee.member.prashantMundada.name') || 'prashant Mundada', roleKey: 'about.committee.role.member', photo: '/images/mundada.jpeg' },
    { name: getText('about.committee.member.shrikantNarke.name') || 'Mr Shrikant Narke', roleKey: 'about.committee.role.member', photo: '/images/shreekant.jpeg' },
    { name: getText('about.committee.member.dadasahebShinalkar.name') || 'Mr Dadasaheb Shinalkar', roleKey: 'about.committee.role.member', photo: '/images/shinalkar.jpeg' },
    { name: getText('about.committee.member.sachinDhumal.name') || 'Mr Sachin Dhumal', roleKey: 'about.committee.role.member', photo: '/images/5.jpg' },
    { name: getText('about.committee.member.sudhirBhadhe.name') || 'Mr Sudhir Bhadhe', roleKey: 'about.committee.role.member', photo: '/images/bhadhe.jpeg' }
  ];

  const allMembers = committeeMembersWithRoles.map((m) => m.name);

  // Fixed distribution across committees (no shuffle)
  const committees = [
    {
      name: getText('about.committee.executive.name'),
      description: getText('about.committee.executive.desc'),
      members: allMembers.slice(0, 3),
      icon: Building,
      theme: 'from-blue-50 to-blue-100',
      iconTheme: 'from-blue-500 to-blue-700'
    },
    {
      name: getText('about.committee.technical.name'),
      description: getText('about.committee.technical.desc'),
      members: allMembers.slice(3, 6),
      icon: Award,
      theme: 'from-emerald-50 to-emerald-100',
      iconTheme: 'from-emerald-500 to-emerald-700'
    },
    {
      name: getText('about.committee.regional.name'),
      description: getText('about.committee.regional.desc'),
      members: allMembers.slice(6, 9),
      icon: MapPin,
      theme: 'from-purple-50 to-purple-100',
      iconTheme: 'from-purple-500 to-purple-700'
    },
    {
      name: getText('about.committee.finance.name'),
      description: getText('about.committee.finance.desc'),
      members: [allMembers[0], allMembers[3], allMembers[6]],
      icon: Users,
      theme: 'from-amber-50 to-amber-100',
      iconTheme: 'from-amber-500 to-amber-700'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden pt-6">
        <div className="container relative z-10 py-8 md:py-12">
          <div className="max-w-5xl mx-auto">
            {/* About Us Button - Top */}
            <div className="mb-4">
              <Link to="/about" className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-800 hover:text-black border-2 border-primary-600 hover:border-primary-700 rounded-lg transition-all duration-300 group shadow-md hover:shadow-lg">
                <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                <span className="font-medium text-sm">{getText('nav.about.submenu.about')}</span>
              </Link>
            </div>
            
            <div className="text-center space-y-4">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight tracking-tight">
                {getText('nav.about.submenu.committee')}
              </h1>
              <p className="text-lg md:text-xl text-blue-100 font-medium">
                {getText('about.committee.subtitle')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Committee Members - Photo, Name, Role */}
      <section className="section py-16 bg-white">
        <div className="container-medium">
          <div className="section-header mb-12">
            <h2 className="section-title text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {getText('about.committee.membersSection.title') || 'Committee Members'}
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-primary-500 to-primary-600 mx-auto mb-4 rounded-full"></div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {getText('about.committee.membersSection.desc')}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {committeeMembersWithRoles.map((member, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-t-4 border-primary-200">
                <div className="mb-4 flex justify-center">
                  {member.photo ? (
                    <div className="relative w-full max-w-[140px] mx-auto">
                      <div className="relative bg-gradient-to-br from-primary-50 to-gray-50 p-2 rounded-xl shadow-lg">
                        <img
                          src={member.photo}
                          alt={member.name}
                          className="w-full rounded-lg object-cover border-2 border-white shadow-md"
                          style={{ aspectRatio: '3/4', maxHeight: '180px' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling?.classList.remove('hidden');
                          }}
                        />
                        <div className="hidden w-full rounded-lg bg-primary-100 flex items-center justify-center border-2 border-white shadow-md" style={{ aspectRatio: '3/4', maxHeight: '180px' }}>
                          <User className="h-12 w-12 text-primary-600" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full max-w-[140px] mx-auto rounded-xl bg-primary-100 p-6 flex items-center justify-center border-2 border-primary-200" style={{ aspectRatio: '3/4', maxHeight: '180px' }}>
                      <User className="h-14 w-14 text-primary-600" />
                    </div>
                  )}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">{member.name}</h3>
                <div className="w-12 h-0.5 bg-gradient-to-r from-primary-500 to-primary-600 mx-auto mb-2 rounded-full"></div>
                <p className="text-primary-600 font-semibold text-sm">{getText(member.roleKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Committee Structure */}
      <section className="section py-16 bg-gray-50">
        <div className="container-medium">
          <div className="section-header mb-12">
            <h2 className="section-title text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {getText('about.committee.structure.title')}
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-primary-500 to-primary-600 mx-auto mb-4 rounded-full"></div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {getText('about.committee.structure.desc')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {committees.map((committee, index) => {
              const Icon = committee.icon;
              return (
                <div key={index} className="group">
                  <div className={`bg-gradient-to-br ${committee.theme} rounded-2xl p-6 lg:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-white/50`}>
                    <div className="flex items-center gap-4 mb-6">
                      <div className={`bg-gradient-to-br ${committee.iconTheme} rounded-xl w-14 h-14 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform ring-4 ring-white/50`}>
                        <Icon className="h-7 w-7 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">{committee.name}</h3>
                        <p className="text-gray-700 text-sm md:text-base">{committee.description}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 pt-4 border-t border-white/50">
                      <h4 className="font-semibold text-gray-900 text-base mb-3">{getText('about.committee.members')}</h4>
                      {committee.members.map((member, memberIndex) => (
                        <div key={memberIndex} className="flex items-center gap-3 p-3 bg-white/70 hover:bg-white rounded-lg transition-colors">
                          <div className="bg-primary-100 p-1.5 rounded-lg">
                            <User className="h-4 w-4 text-primary-600" />
                          </div>
                          <span className="text-gray-800 text-sm font-medium">{member}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Committee Responsibilities */}
      <section className="section py-16 bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
        <div className="container-medium">
          <div className="section-header mb-12">
            <h2 className="section-title text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {getText('about.committee.responsibilities.title')}
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-primary-500 to-primary-600 mx-auto mb-4 rounded-full"></div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {getText('about.committee.responsibilities.desc')}
            </p>
          </div>
          
          <div className="space-y-8">
            {committees.map((committee, index) => {
              const Icon = committee.icon;
              const getResponsibilities = (committeeName) => {
                if (committeeName === getText('about.committee.executive.name')) {
                  return [
                    getText('about.committee.responsibility.executive.1'),
                    getText('about.committee.responsibility.executive.2'),
                    getText('about.committee.responsibility.executive.3'),
                    getText('about.committee.responsibility.executive.4')
                  ];
                } else if (committeeName === getText('about.committee.technical.name')) {
                  return [
                    getText('about.committee.responsibility.technical.1'),
                    getText('about.committee.responsibility.technical.2'),
                    getText('about.committee.responsibility.technical.3'),
                    getText('about.committee.responsibility.technical.4')
                  ];
                } else if (committeeName === getText('about.committee.regional.name')) {
                  return [
                    getText('about.committee.responsibility.regional.1'),
                    getText('about.committee.responsibility.regional.2'),
                    getText('about.committee.responsibility.regional.3'),
                    getText('about.committee.responsibility.regional.4')
                  ];
                } else if (committeeName === getText('about.committee.finance.name')) {
                  return [
                    getText('about.committee.responsibility.finance.1'),
                    getText('about.committee.responsibility.finance.2'),
                    getText('about.committee.responsibility.finance.3'),
                    getText('about.committee.responsibility.finance.4')
                  ];
                }
                return [];
              };
              
              const responsibilities = getResponsibilities(committee.name);

              return (
                <div key={index} className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                  <div className="flex items-start gap-6">
                    <div className={`bg-gradient-to-br ${committee.iconTheme} rounded-xl w-16 h-16 flex items-center justify-center flex-shrink-0 shadow-xl ring-4 ring-white`}>
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{committee.name}</h3>
                      <p className="text-gray-600 mb-6">{committee.description}</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {responsibilities.map((responsibility, respIndex) => (
                          <div key={respIndex} className="flex items-start gap-3 p-4 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all">
                            <div className="bg-primary-100 p-2 rounded-lg flex-shrink-0">
                              <Calendar className="h-4 w-4 text-primary-600" />
                            </div>
                            <span className="text-gray-700 text-sm leading-relaxed">{responsibility}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

    </div>
  );
};

export default CommitteePage;
